import { useEffect } from 'react';

/**
 * Singleton state to track scroll locks app-wide.
 * Prevents multiple locks from corrupting the "original" state.
 */
const lockState = {
    count: 0,
    originalStyles: {
        position: '',
        top: '',
        width: '',
        height: '',
        overflow: ''
    },
    scrollY: 0
};

const preventDefault = (e: TouchEvent) => {
    // Check if the touch is occurring within an element that is allowed to scroll
    let target = e.target as HTMLElement | null;
    while (target && target !== document.body) {
        if (target.classList.contains('allow-scroll')) {
            return; // Allow the scroll
        }
        target = target.parentElement;
    }

    // If it's a multi-touch (pinch) we might want to allow it for camera zoom, 
    // but the listener is on the body, so it won't affect the specific camera div listeners.
    if (e.touches.length === 1) {
        e.preventDefault();
    }
};

/**
 * Universal "Nuclear" Scroll Lock for Android, iOS, and Mobile Web.
 * Uses the Fixed-Position method + Passive Touch listener prevention.
 * Refactored to be nestable/stackable.
 * @param isLocked Whether the scroll should be locked.
 */
export const useScrollLock = (isLocked: boolean) => {
    useEffect(() => {
        if (isLocked) {
            lockState.count++;

            // Only capture and apply on the first lock
            if (lockState.count === 1) {
                // 1. Capture current scroll position
                lockState.scrollY = window.scrollY;
                
                // 2. Lock the body as a fixed layer
                lockState.originalStyles = {
                    position: document.body.style.position,
                    top: document.body.style.top,
                    width: document.body.style.width,
                    height: document.body.style.height,
                    overflow: document.body.style.overflow
                };

                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
                document.body.style.height = '100%';
                document.body.style.top = `-${lockState.scrollY}px`;
                document.body.style.overflow = 'hidden';

                // 3. Prevent touch-move momentum leaks
                document.addEventListener('touchmove', preventDefault, { passive: false });
            }

            return () => {
                lockState.count--;

                // Only restore on the final unlock
                if (lockState.count === 0) {
                    // 4. Restore everything
                    document.body.style.position = lockState.originalStyles.position;
                    document.body.style.width = lockState.originalStyles.width;
                    document.body.style.height = lockState.originalStyles.height;
                    document.body.style.top = lockState.originalStyles.top;
                    document.body.style.overflow = lockState.originalStyles.overflow;
                    
                    // Remove the touch block
                    document.removeEventListener('touchmove', preventDefault);

                    // 5. Instantly jump back to the saved scroll position
                    window.scrollTo(0, lockState.scrollY);
                }
            };
        }
    }, [isLocked]);
};
